import { useRef } from 'react';
import type { TokenEntry } from '@/lib/designTokenParser';
import { updateTokenInMarkdown, isHexColor } from '@/lib/designTokenParser';

interface ColorTokenGroupProps {
  title: string;
  tokens: TokenEntry[];
  markdown: string;
  onMarkdownChange: (md: string) => void;
}

const cardStyle: React.CSSProperties = {
  padding: '10px',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-card-surface)',
};

const nameStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const commentStyle: React.CSSProperties = {
  fontSize: '10px',
  color: 'var(--text-muted)',
  marginTop: '4px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const hexInputStyle: React.CSSProperties = {
  padding: '2px 6px',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '11px',
  fontFamily: 'monospace',
  width: '80px',
  boxSizing: 'border-box',
};

export function ColorTokenGroup({ title, tokens, markdown, onMarkdownChange }: ColorTokenGroupProps) {
  if (tokens.length === 0) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <h4 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px', marginTop: 0 }}>
        {title}
      </h4>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '8px',
      }}>
        {tokens.map((token) => (
          <ColorCard
            key={`${token.lineIndex}-${token.name}`}
            token={token}
            markdown={markdown}
            onMarkdownChange={onMarkdownChange}
          />
        ))}
      </div>
    </div>
  );
}

function ColorCard({ token, markdown, onMarkdownChange }: {
  token: TokenEntry;
  markdown: string;
  onMarkdownChange: (md: string) => void;
}) {
  const pickerRef = useRef<HTMLInputElement>(null);
  const isValid = isHexColor(token.value);

  const handleColorChange = (newValue: string) => {
    onMarkdownChange(updateTokenInMarkdown(token.lineIndex, newValue, markdown));
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <div
          onClick={() => pickerRef.current?.click()}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            backgroundColor: isValid ? token.value : 'var(--bg-secondary)',
            border: '1px solid var(--color-border)',
            cursor: 'pointer',
            flexShrink: 0,
            backgroundImage: !isValid ? 'repeating-linear-gradient(45deg, var(--color-border) 25%, transparent 25%, transparent 75%, var(--color-border) 75%), repeating-linear-gradient(45deg, var(--color-border) 25%, transparent 25%, transparent 75%, var(--color-border) 75%)' : undefined,
            backgroundSize: !isValid ? '8px 8px' : undefined,
            backgroundPosition: !isValid ? '0 0, 4px 4px' : undefined,
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={nameStyle}>{token.name}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <input
          style={hexInputStyle}
          value={token.value}
          onChange={(e) => handleColorChange(e.target.value)}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && !isHexColor(v)) {
              // Revert to original if invalid — user sees no change
            }
          }}
        />
        <button
          type="button"
          onClick={() => pickerRef.current?.click()}
          style={{
            padding: '2px 6px',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            backgroundColor: 'var(--bg-secondary)',
            cursor: 'pointer',
            fontSize: '13px',
            lineHeight: 1,
          }}
          title="Pick color"
        >
          &#127912;
        </button>
        <input
          ref={pickerRef}
          type="color"
          value={isValid ? token.value : '#000000'}
          onChange={(e) => handleColorChange(e.target.value)}
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
        />
      </div>
      {token.comment && <div style={commentStyle} title={token.comment}>{token.comment}</div>}
    </div>
  );
}
